import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CopyIconButton } from '~/components/common/copy-icon-button';
import {
  AttributeSearch,
  DiffLegend,
  PairReplacedToggle,
  SyncScrollToggle,
} from '~/components/common/product-details-comparison/attribute-diff-controls';
import {
  AttributeDiffRowAligned,
  DiffPane,
} from '~/components/common/product-details-comparison/attribute-diff-row';
import { AttributeSectionBand } from '~/components/common/product-details-comparison/attribute-section-band';
import { useFeatureGate } from '~/hooks/useFeatureGate';
import type { ProductDetailAttribute } from '~/types/enrichment';
import type { AttributeDiffRow, AttributeDiffSection, DiffStatus } from '~/types/product-diff';
import { cn } from '~/utils/cn';
import {
  NOT_CARRIED_OVER,
  buildAttributeDiff,
  flattenSections,
  hasReplacementLinks,
  rowMatchesSearch,
  tallyStatuses,
} from '~/utils/product-diff';

/** Client-side filter over an already-loaded array, so this only has to outrun
 *  typing — the project's 500ms convention exists for server-backed search. */
const SEARCH_DEBOUNCE_MS = 150;

interface ProductDetailsComparisonProps {
  /** Current (pre-enrichment) structured attribute list. */
  currentAttrs: ProductDetailAttribute[];
  /** Enriched (post-enrichment) structured attribute list. */
  enrichedAttrs: ProductDetailAttribute[];
  /** `'panels'` (default) — two height-capped columns, scroll-linked by default
   *  via the "Sync scroll" toggle (off scrolls each side on its own).
   *  `'aligned'` — one grid row per attribute, with a single shared scroller
   *  under pinned headers, so the two sides can't drift apart when a value wraps. */
  layout?: 'panels' | 'aligned';
  /** Attribute name this table belongs to (e.g. "Product Details") — names the
   *  column copy buttons so their tooltip/toast says what was copied. */
  label?: string;
  /** Panels layout only: render the attribute search. Off by default so the
   *  compact-preview surfaces stay lean (a test pins that); the product detail
   *  page opts in to keep the jump-to-attribute it had in the aligned layout.
   *  The aligned layout always shows search regardless of this flag. */
  showSearch?: boolean;
  /** Panels layout only: drop the "Current" column and render an enriched-only
   *  view (the product detail page's PDP-preview toggle). Rows with no enriched
   *  value are filtered out and the single Lily panel spans the full width. */
  hideCurrent?: boolean;
  /** External status allow-list, e.g. a parent's field-status filter. When set,
   *  only rows whose status is included render (ANDed with the in-panel legend +
   *  search). Omit for no external constraint. */
  restrictToStatuses?: DiffStatus[] | undefined;
  /** `data-testid` root; derives search, legend, panel, section and row ids (see docs/test-ids.md). */
  testId?: string;
}

/** Kebab-cases a section key / row id so it can key a `data-testid`. */
function toTestIdSlug(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

/** Rows that survive the section, status and search filters, regrouped into the
 *  bands they belong to. Sections left with no visible row are dropped rather
 *  than rendered as an empty band. */
function filterSections(
  sections: AttributeDiffSection[],
  predicate: (row: AttributeDiffRow) => boolean,
): AttributeDiffSection[] {
  return sections
    .map((section) => ({ ...section, rows: section.rows.filter(predicate) }))
    .filter((section) => section.rows.length > 0);
}

/**
 * Side-by-side attribute diff, grouped by the optimized payload's `section_name`
 * taxonomy. Current values on the left, Lily AI optimized values on the right,
 * one band per section, and a status per row.
 *
 * Two layouts. `'panels'` renders two independently-flowing columns, each capped
 * at `max-h-100`; their scroll is linked by default (an `isSyncing` guard ref
 * breaks the mutual onScroll loop) and the "Sync scroll" toggle lets the reviewer
 * unlink them. Its section bands repeat in both columns, which stay level
 * because the rows are one-to-one. `'aligned'` renders one grid row per attribute
 * and scrolls *once* for both sides, so a band can span the full width and a
 * value that wraps can't push one side out of step with the other.
 */
export function ProductDetailsComparison({
  currentAttrs,
  enrichedAttrs,
  layout = 'panels',
  label,
  showSearch = false,
  hideCurrent = false,
  restrictToStatuses,
  testId = 'product-details-comparison',
}: ProductDetailsComparisonProps) {
  const { t } = useTranslation('common');
  const isAligned = layout === 'aligned';

  // Pairing needs both a payload that states which attribute replaces which and
  // an enabled gate. Neither is true today, so `replaced` rows are unreachable
  // and renames still render as a removal plus an addition.
  const { isAllowed: isPairingAllowed } = useFeatureGate('pair_attribute_replacements');
  const canPair = isPairingAllowed && hasReplacementLinks(enrichedAttrs);
  const [pairReplacements, setPairReplacements] = useState(true);

  // Derivation is memoised on the payload references and the pairing choice —
  // never on the search term, so grouping can't run per keystroke.
  const sections = useMemo(
    () =>
      buildAttributeDiff(currentAttrs, enrichedAttrs, {
        pairReplacements: canPair ? pairReplacements : true,
      }),
    [currentAttrs, enrichedAttrs, canPair, pairReplacements],
  );
  const allRows = useMemo(() => flattenSections(sections), [sections]);
  const counts = useMemo(() => tallyStatuses(allRows), [allRows]);

  /** Section name for display — the removed-attribute bucket is a sentinel key,
   *  everything else is the payload's own section name. */
  function sectionLabel(key: string): string {
    return key === NOT_CARRIED_OVER ? t('productDetailsComparison.notCarriedOver') : key;
  }

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [statusFilter, setStatusFilter] = useState<DiffStatus | null>(null);
  const [collapsedSections, setCollapsedSections] = useState(new Set<string>());

  function handleSearchChange(value: string) {
    setSearch(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(value);
    }, SEARCH_DEBOUNCE_MS);
  }

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    [],
  );

  const needle = debouncedSearch.trim().toLowerCase();

  // Filters the derived rows; the section grouping above is never recomputed.
  const visibleSections = useMemo(
    () =>
      filterSections(
        sections,
        (row) =>
          (restrictToStatuses == null || restrictToStatuses.includes(row.status)) &&
          (statusFilter === null || row.status === statusFilter) &&
          rowMatchesSearch(row, needle) &&
          // Enriched-only view: keep only rows that actually have a Lily value, so
          // removed/not-carried-over attributes don't render as empty placeholders.
          (!hideCurrent || row.after != null),
      ),
    [sections, statusFilter, needle, hideCurrent, restrictToStatuses],
  );
  const visibleCount = useMemo(() => flattenSections(visibleSections).length, [visibleSections]);

  /** Applies a status as the row filter, or clears it when already active. */
  function toggleStatusFilter(status: DiffStatus) {
    setStatusFilter((current) => (current === status ? null : status));
  }

  function toggleCollapsed(key: string) {
    setCollapsedSections((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  const copyTarget = label ?? t('productDetailsComparison.copyFallbackLabel');
  const currentJson = useMemo(() => JSON.stringify(currentAttrs, null, 2), [currentAttrs]);
  const enrichedJson = useMemo(() => JSON.stringify(enrichedAttrs, null, 2), [enrichedAttrs]);
  const isFiltered = needle !== '' || statusFilter !== null || restrictToStatuses != null;
  // "Nothing to compare" and "nothing matched your filters" are different facts,
  // and only one of them is the reviewer's to fix.
  const emptyMessage =
    allRows.length === 0
      ? t('productDetailsComparison.noAttributes')
      : t('productDetailsComparison.noSearchMatch');

  const leftRef = useRef<HTMLDivElement>(null);
  const rightRef = useRef<HTMLDivElement>(null);
  // Prevents the reciprocal onScroll from re-triggering an infinite loop.
  const isSyncing = useRef(false);
  // Whether the two columns scroll together. On by default (the values stay
  // level for comparison); the reviewer can turn it off to scroll one side alone.
  const [syncScroll, setSyncScroll] = useState(true);

  function handleLeftScroll() {
    if (!syncScroll || isSyncing.current || !leftRef.current || !rightRef.current) return;
    isSyncing.current = true;
    rightRef.current.scrollTop = leftRef.current.scrollTop;
    isSyncing.current = false;
  }

  function handleRightScroll() {
    if (!syncScroll || isSyncing.current || !leftRef.current || !rightRef.current) return;
    isSyncing.current = true;
    leftRef.current.scrollTop = rightRef.current.scrollTop;
    isSyncing.current = false;
  }

  /** Region id for a band's `aria-controls`. Per side, because the two panels
   *  render their own copy of each section's rows. */
  function regionId(sectionKey: string, side: 'before' | 'after' | 'row'): string {
    return `attr-diff-${side}-${sectionKey.replace(/\W+/g, '-')}`;
  }

  const columnHeaders = (
    <>
      <p className="text-muted-foreground text-xs font-medium">
        {t('productDetailsComparison.current')}
      </p>
      <CopyIconButton
        testId={`${testId}-copy-current-button`}
        text={currentJson}
        label={t('productDetailsComparison.copyCurrent', { label: copyTarget })}
        copyMessage={t('productDetailsComparison.copiedCurrent', { label: copyTarget })}
      />
    </>
  );

  if (isAligned) {
    return (
      <div className="overflow-hidden rounded-xl border" data-testid={testId}>
        {/* Controls strip — this layout shows every attribute at once, so a
            product carrying dozens of them needs a way to jump to one. Inside the
            table's border so it reads as belonging to this table, not the page.
            Section membership is navigated through the bands themselves, which is
            why there is no per-section filter here. */}
        <div className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-2.5">
          <AttributeSearch
            value={search}
            onChange={handleSearchChange}
            testId={`${testId}-search-input`}
          />
          <div className="flex flex-wrap items-center gap-2">
            {isFiltered && (
              <p className="text-muted-foreground text-xs" data-testid={`${testId}-filtered-count`}>
                {t('productDetailsComparison.filteredCount', {
                  visible: visibleCount,
                  total: allRows.length,
                })}
              </p>
            )}
            <DiffLegend
              counts={counts}
              active={statusFilter}
              onToggle={toggleStatusFilter}
              testId={`${testId}-legend`}
            />
            {canPair && (
              <PairReplacedToggle
                checked={pairReplacements}
                onChange={setPairReplacements}
                testId={`${testId}-pair-replaced-checkbox`}
              />
            )}
          </div>
        </div>
        {/* Column headers, once for the whole table — each owns the copy button
            for its own side, so the affordance sits on the thing it copies. */}
        <div className="bg-surface-blue grid grid-cols-1 border-b md:grid-cols-2">
          <div className="flex items-center justify-between gap-2 px-4 py-1.5">{columnHeaders}</div>
          <div className="border-primary/30 flex items-center justify-between gap-2 px-4 py-1.5 md:border-l">
            <p className="text-primary text-xs font-medium">
              {t('productDetailsComparison.lilyOptimization')}{' '}
              <span className="font-normal opacity-70">
                {t('productDetailsComparison.groupedIntoSections', { count: sections.length })}
              </span>
            </p>
            <CopyIconButton
              testId={`${testId}-copy-enriched-button`}
              text={enrichedJson}
              label={t('productDetailsComparison.copyEnriched', { label: copyTarget })}
              copyMessage={t('productDetailsComparison.copiedEnriched', { label: copyTarget })}
            />
          </div>
        </div>
        {/* Only the rows scroll: the controls and column headers sit above this
            container, so the filters and the header the values belong to stay put
            however far down the attribute list the operator is. `auto`, not
            `scroll` — a short product must not show a dead track. */}
        <div className="max-h-150 overflow-y-auto">
          {visibleSections.length === 0 && (
            <p
              className="text-muted-foreground px-4 py-8 text-center text-sm"
              data-testid={`${testId}-empty`}
            >
              {emptyMessage}
            </p>
          )}
          {visibleSections.map((section) => {
            const isOpen = !collapsedSections.has(section.section);
            const id = regionId(section.section, 'row');
            const sectionTestId = `${testId}-section-${toTestIdSlug(section.section)}`;
            return (
              <div key={section.section} data-testid={sectionTestId}>
                <AttributeSectionBand
                  testId={`${sectionTestId}-toggle-button`}
                  label={sectionLabel(section.section)}
                  rowCount={section.rows.length}
                  counts={section.counts}
                  isOpen={isOpen}
                  onToggle={() => {
                    toggleCollapsed(section.section);
                  }}
                  regionId={id}
                />
                {/* The region element is always present so the band's
                    `aria-controls` resolves, but a collapsed section drops its
                    rows rather than hiding them — `hidden` would leave them in
                    the text and accessibility trees. */}
                <div id={id} className="divide-y">
                  {isOpen &&
                    section.rows.map((row) => (
                      <AttributeDiffRowAligned
                        key={row.id}
                        row={row}
                        testId={`${testId}-row-${toTestIdSlug(row.id)}`}
                      />
                    ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  /** One column of the panels layout: its own band per section, its own scroller.
   *  Both columns render the same sections in the same order, so the bands sit
   *  level and the scroll-sync keeps them there. */
  function renderPanelColumn(side: 'before' | 'after') {
    return visibleSections.map((section) => {
      const isOpen = !collapsedSections.has(section.section);
      const id = regionId(section.section, side);
      const sideName = side === 'before' ? 'current' : 'enriched';
      const sectionTestId = `${testId}-${sideName}-section-${toTestIdSlug(section.section)}`;
      return (
        <div key={section.section} data-testid={sectionTestId}>
          <AttributeSectionBand
            testId={`${sectionTestId}-toggle-button`}
            label={sectionLabel(section.section)}
            rowCount={section.rows.length}
            counts={section.counts}
            isOpen={isOpen}
            onToggle={() => {
              toggleCollapsed(section.section);
            }}
            regionId={id}
            // The ledger belongs with the optimized values it describes; repeating
            // it on the current side would state the same tally twice.
            showLedger={side === 'after'}
          />
          <div id={id} className="divide-y">
            {isOpen &&
              section.rows.map((row) => (
                <DiffPane
                  key={row.id}
                  row={row}
                  side={side}
                  testId={`${testId}-row-${toTestIdSlug(row.id)}-${sideName}`}
                />
              ))}
          </div>
        </div>
      );
    });
  }

  return (
    // No `overflow-hidden`/`rounded-xl` here: this wrapper has no border or
    // background of its own (both panels round and clip themselves), so all it
    // could ever clip is the legend's focus ring, which sits at its top edge.
    <div data-testid={testId}>
      {/* Legend sits with the table rather than in the host's accordion header,
          so the key travels with the colours it explains on every surface.
          `pt-1` because the host renders this flush against `CollapsibleContent`'s
          permanent `overflow-hidden` edge, which would otherwise shave the top off
          a focused chip's ring. */}
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2 pt-1">
        {/* Search is opt-in so the compact-preview surfaces stay lean; the product
            detail page turns it on to keep the jump-to-attribute it had in the
            aligned layout. The empty span keeps the right cluster right-aligned
            when search is off. The sync toggle governs the two columns' scroll and
            sits apart from the colour key, which filters rows. */}
        {showSearch ? (
          <AttributeSearch
            value={search}
            onChange={handleSearchChange}
            testId={`${testId}-search-input`}
          />
        ) : (
          <span aria-hidden="true" />
        )}
        <div className="flex flex-wrap items-center gap-2">
          {isFiltered && (
            <p className="text-muted-foreground text-xs" data-testid={`${testId}-filtered-count`}>
              {t('productDetailsComparison.filteredCount', {
                visible: visibleCount,
                total: allRows.length,
              })}
            </p>
          )}
          {/* Sync-scroll only makes sense with two columns — hide it in the
              enriched-only view. */}
          {!hideCurrent && (
            <SyncScrollToggle
              checked={syncScroll}
              onChange={setSyncScroll}
              testId={`${testId}-sync-scroll-toggle`}
            />
          )}
          <DiffLegend
            counts={counts}
            active={statusFilter}
            onToggle={toggleStatusFilter}
            testId={`${testId}-legend`}
          />
        </div>
      </div>
      <div
        className={cn(
          'grid grid-cols-1 gap-3 transition-[grid-template-columns,gap] duration-300 ease-in-out motion-reduce:transition-none',
          hideCurrent ? 'md:grid-cols-[0fr_1fr] md:gap-0' : 'md:grid-cols-[1fr_1fr]',
        )}
      >
        {/* Current panel — collapses out (height when stacked, width at ≥md) in
            the enriched-only view. */}
        <div
          className={cn(
            'grid overflow-hidden transition-[grid-template-rows,opacity] duration-300 ease-in-out motion-reduce:transition-none md:block',
            hideCurrent ? 'grid-rows-[0fr] opacity-0' : 'grid-rows-[1fr] opacity-100',
          )}
          aria-hidden={hideCurrent}
        >
          <div
            className="min-w-0 overflow-hidden rounded-xl border"
            data-testid={`${testId}-current-panel`}
          >
            <div className="bg-surface-blue flex items-center justify-between gap-2 border-b px-4 py-1.5">
              {columnHeaders}
            </div>
            <div ref={leftRef} onScroll={handleLeftScroll} className="max-h-100 overflow-y-scroll">
              {visibleSections.length === 0 && (
                <p
                  className="text-muted-foreground px-4 py-8 text-center text-sm"
                  data-testid={`${testId}-current-empty`}
                >
                  {emptyMessage}
                </p>
              )}
              {renderPanelColumn('before')}
            </div>
          </div>
        </div>

        {/* Lily AI Optimization panel */}
        <div
          className="border-primary min-w-0 overflow-hidden rounded-xl border"
          data-testid={`${testId}-enriched-panel`}
        >
          <div className="bg-surface-blue flex items-center justify-between gap-2 border-b px-4 py-1.5">
            <p className="text-primary text-xs font-medium">
              {t('productDetailsComparison.lilyOptimization')}{' '}
              <span className="font-normal opacity-70">
                {t('productDetailsComparison.groupedIntoSections', { count: sections.length })}
              </span>
            </p>
            <CopyIconButton
              testId={`${testId}-copy-enriched-button`}
              text={enrichedJson}
              label={t('productDetailsComparison.copyEnriched', { label: copyTarget })}
              copyMessage={t('productDetailsComparison.copiedEnriched', { label: copyTarget })}
            />
          </div>
          <div ref={rightRef} onScroll={handleRightScroll} className="max-h-100 overflow-y-scroll">
            {visibleSections.length === 0 && (
              <p
                className="text-muted-foreground px-4 py-8 text-center text-sm"
                data-testid={`${testId}-enriched-empty`}
              >
                {emptyMessage}
              </p>
            )}
            {renderPanelColumn('after')}
          </div>
        </div>
      </div>
    </div>
  );
}
